import { useEffect, useRef } from 'react';
import { pushGA4Event } from '@/utils/analytics';
import usePageData, { useOldJwt } from '@/hooks/use-data/use-data';
import { findLocationByName, PagePlatform } from '@rental/prebooking-ui-utils';
import { STEPS } from '@/components/forms/steps/step-flow';

/**
 * Hook to track page changes and send GA4 events
 * This hook will send a GA4 event whenever the page changes
 */
const useGA4PageTracking = () => {
  const previousPathname = useRef<string | null>(null);
  const { webView, cityName, language } = usePageData();
  const jwt = useOldJwt();

  useEffect(() => {
    // Function to send page change event
    const sendPageChangeEvent = () => {
      if (typeof window === 'undefined') return;

      const currentPath = window.location.pathname;

      // Only send event if the path has changed
      if (previousPathname.current !== currentPath) {
        // Extract page name from path
        const pathParts = currentPath.split('/').filter(Boolean);
        const pageName = pathParts.length > 0 ? pathParts[pathParts.length - 1] : 'home';

        // Determine form step based on the page name
        let formStep = pageName;

        // Map URL paths to specific form steps if needed
        const stepMapping: Record<string, string> = {
          'trip': STEPS.TRIP,
          'category': STEPS.CATEGORY,
          'extras': STEPS.EXTRAS,
          'personal': STEPS.PERSONAL,
          'overview': STEPS.OVERVIEW,
          'service': STEPS.SERVICE,
          // Add other mappings as needed
        };

        if (stepMapping[pageName]) {
          formStep = stepMapping[pageName];
        }

        // Send GA4 event with specific format for page changes
        const eventName = `prebooking_${pageName}_page_view`;

        const eventParams: Record<string, any> = {
          page_path: currentPath,
          page_title: document.title,
          page_location: window.location.href,
          form_step: formStep,
          pagePlatform: webView ? PagePlatform.app : PagePlatform.web,
        };

        // Only include location data if cityName is available
        if (cityName) {
          const location = findLocationByName(cityName);
          if (location) {
            eventParams.country = location.country;
            eventParams.language = language;
            eventParams.cityName = cityName;
          }
        }

        // Include JWT if available
        if (jwt) {
          eventParams.jwt = jwt;
        }

        // Send the GA4 event
        pushGA4Event(eventName, eventParams);

        // Update previous pathname
        previousPathname.current = currentPath;
      }
    };

    // Only run in browser environment
    if (typeof window !== 'undefined') {
      // Send event on initial load
      sendPageChangeEvent();

      // Add event listener for history changes
      const handleHistoryChange = () => {
        sendPageChangeEvent();
      };

      window.addEventListener('popstate', handleHistoryChange);

      // Monkey patch history methods to track SPA navigation
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;

      history.pushState = function(...args) {
        originalPushState.apply(this, args);
        handleHistoryChange();
      };

      history.replaceState = function(...args) {
        originalReplaceState.apply(this, args);
        handleHistoryChange();
      };

      // Cleanup
      return () => {
        window.removeEventListener('popstate', handleHistoryChange);
        history.pushState = originalPushState;
        history.replaceState = originalReplaceState;
      };
    }
  }, [cityName, language, webView, jwt]);
};

export default useGA4PageTracking;
